import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { SocialAuthButtons } from '@/components/SocialAuthButtons';

export default function RegisterPage() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        display_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role: 'sub',
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setLoading(true);
        try {
            await register(form);
            navigate('/');
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
                if (err.errors) setFieldErrors(err.errors);
            } else {
                setError('Registration failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const update = (field: string, value: string) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    return (
        <Card>
            <CardHeader className="items-center text-center">
                <img src="/images/monogram.png" alt="DebtBound" className="h-12 w-12 mb-2" />
                <CardTitle>Create Account</CardTitle>
            </CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="space-y-4">
                    {error && (
                        <div className="rounded-md bg-red-900/30 p-3 text-sm text-red-400">
                            {error}
                        </div>
                    )}
                    <div>
                        <label className="mb-1 block text-sm font-medium">Display Name</label>
                        <Input
                            value={form.display_name}
                            onChange={(e) => update('display_name', e.target.value)}
                            autoFocus
                        />
                        {fieldErrors.display_name && (
                            <p className="mt-1 text-xs text-red-500">{fieldErrors.display_name[0]}</p>
                        )}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Email</label>
                        <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => update('email', e.target.value)}
                            required
                        />
                        {fieldErrors.email && (
                            <p className="mt-1 text-xs text-red-500">{fieldErrors.email[0]}</p>
                        )}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Role</label>
                        <select
                            value={form.role}
                            onChange={(e) => update('role', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                        >
                            <option value="sub">Sub</option>
                            <option value="dom">Dom</option>
                            <option value="switch">Switch</option>
                            <option value="admin">Admin</option>
                            <option value="moderator">Moderator</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Password</label>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={(e) => update('password', e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Confirm Password</label>
                        <Input
                            type="password"
                            value={form.password_confirmation}
                            onChange={(e) => update('password_confirmation', e.target.value)}
                            required
                        />
                    </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-3">
                    <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Account'}
                    </Button>
                    <SocialAuthButtons />
                    <p className="text-center text-sm text-slate-400">
                        Already have an account?{' '}
                        <Link to="/login" className="text-violet-400 hover:underline">
                            Sign in
                        </Link>
                    </p>
                </CardFooter>
            </form>
        </Card>
    );
}
